import { useNavigate, useParams } from "react-router-dom";
import useApi from 'helpers/api';
import { useEffect, useRef, useState } from "react";
import useNotification from "hooks/useNotification";
import ServiceCatalogService from "services/ServiceCatalog.service";
import TaskTemplateService from "services/TaskTemplate.service";
import { faCopy, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import SuccessRequest from "components/requests-response/SuccessRequest";
import FailedRequest from "components/requests-response/FailedRequest";
import { Button, Card, Col, Form, Row, } from "react-bootstrap";
import FalconComponentCard from "components/common/FalconComponentCard";
import Flex from "components/common/Flex";
import IconButton from "components/common/IconButton";
import Skeleton from "react-loading-skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MultiSelect from "components/common/MultiSelect";
import TreeSelect from "components/common/TreeSelect";
import TemplateTaskSection from "components/tickets/TemplateTaskSection";
import TaskTemplateExplanation from "./TaskTemplateExplanation";
import TaskTemplateItemService from "services/TaskTemplateItem.service";

const TaskTemplateClone = () => {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [serviceCatalogs, setServiceCatalogs] = useState([]);
  const [customers, setCustomers] = useState({
    currentPage: 0,
    hasNextPage: false,
    totalPages: 0,
    totalElementsPerPage: 20,
    totalElements: 0,
    content: []
  });

  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [allClientsSelected, setAllClientsSelected] = useState(true);
  const [selectedClients, setSelectedClients] = useState([]);
  const [initialTasks, setInitialTasks] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);

  const [handleModals, setHandleModals] = useState({
    categoryRegistration: false,
    categoryEdit: false,
    subcategoryRegistration: false,
    subcategoryEdit: false,
    error: false,
    success: false,
    dialogConfirm: false
  });
  const [error, setError] = useState({});
  const [success, setSuccess] = useState('');
  const [validated, setValidated] = useState(false);
  const form = useRef(null);
  const [body, setBody] = useState({
    title: '',
    description: '',
    serviceCatalog: { id: 0 },
    category: { id: 0 },
    subcategory: { id: 0 }
  });

  const taskSectionRef = useRef();

  useEffect(() => {
    const initializeComponent = async () => {
      await Promise.all([
        fetchServiceCatalogs(),
        fetchCustomers()
      ]);

      if (id) {
        await loadTaskTemplateForClone(id);
      }
    };

    initializeComponent();
  }, [id]);

  const fetchServiceCatalogs = async () => {
    try {
      const response = await ServiceCatalogService.listInTreeView();
      setServiceCatalogs(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching service catalogs:', error);
      showErrorNotification('Erro ao carregar catálogos de serviço');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.customers.list();
      setCustomers(response || {
        currentPage: 0,
        hasNextPage: false,
        totalPages: 0,
        totalElementsPerPage: 20,
        totalElements: 0,
        content: []
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
      showErrorNotification('Erro ao carregar clientes');
    }
  };

  const loadTaskTemplateForClone = async (templateId) => {
    setIsLoading(true);
    try {
      const [templateResponse, tasksResponse] = await Promise.all([
        TaskTemplateService.findById(parseInt(templateId)),
        TaskTemplateItemService.listItems(parseInt(templateId))
      ]);

      if (templateResponse && !templateResponse.error) {
        const template = templateResponse;

        setBody({
          title: `${template.title.includes("- Cópia") ? template.title : template.title + " - Cópia"}`,
          description: template.description || '',
          serviceCatalog: { id: template.serviceCatalog?.id || 0 },
          category: { id: template.category?.id || 0 },
          subcategory: { id: template.subcategory?.id || 0 }
        });

        if (template.serviceCatalog?.id && template.category?.id && template.subcategory?.id) {
          setSelectedSubcategory({
            serviceCatalogId: template.serviceCatalog.id,
            categoryId: template.category.id,
            id: template.subcategory.id
          });
        }

        setAllClientsSelected(!!template.applyToAllCustomers);
        setSelectedClients(template.applyToAllCustomers ? [] : (template.customers?.map(customer => customer.id) || []));
      }

      if (tasksResponse && !tasksResponse.error) {
        const tasks = Array.isArray(tasksResponse) ? tasksResponse : tasksResponse.content || [];

        // Format tasks with validation para clonagem
        const formattedTasks = tasks.map((task, index) => ({
          id: `temp-${index}-${Date.now()}`,
          title: task.title || '',
          description: task.description || '',
          isGroupTitle: !!task.isGroupTitle,
          days_to_complete: task.deadlineDays === null || task.deadlineDays === undefined ? null : task.deadlineDays,
          deadlineDays: task.deadlineDays === null || task.deadlineDays === undefined ? null : task.deadlineDays,
          agent: task.agent ? {
            id: task.agent.id,
            firstName: task.agent.firstName || '',
            lastName: task.agent.lastName || '',
            name: `${task.agent.firstName || ''} ${task.agent.lastName || ''}`.trim()
          } : null,
          status: !!task.status,
          isTemporary: true
        }));

        console.log('Formatted tasks for cloning:', formattedTasks);
        setInitialTasks(formattedTasks);
      } else {
        console.warn('Erro ao carregar tasks ou nenhuma task encontrada');
        setInitialTasks([]);
      }
      setTasksLoaded(true);
    } catch (error) {
      console.error('Error loading task template for clone:', error);
      showErrorNotification('Erro ao carregar template para clonagem');
      setInitialTasks([]); // Ensure fallback
      setTasksLoaded(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetBody = (field, value) => {
    setBody(prevBody => ({ ...prevBody, [field]: value }));
  };

  const handleSelectServiceCatalog = (selectedValue) => {
    if (selectedValue && Array.isArray(selectedValue) && selectedValue.length === 3) {
      const [serviceCatalogId, categoryId, subcategoryId] = selectedValue;
      if (serviceCatalogId && categoryId && subcategoryId) {
        setBody(prev => ({
          ...prev,
          serviceCatalog: { id: serviceCatalogId },
          category: { id: categoryId },
          subcategory: { id: subcategoryId }
        }));
        setSelectedSubcategory({
          serviceCatalogId,
          categoryId,
          id: subcategoryId
        });
        return;
      }
    }
    clearSubcategorySelection();
  };

  const clearSubcategorySelection = () => {
    setBody(prev => ({
      ...prev,
      serviceCatalog: { id: 0 },
      category: { id: 0 },
      subcategory: { id: 0 }
    }));
    setSelectedSubcategory(null);
  };

  const toggleModal = (modalName, value) => {
    setHandleModals(prevState => ({ ...prevState, [modalName]: value }));
  };

  const toggleAllClients = () => {
    setAllClientsSelected(prev => !prev);
    if (!allClientsSelected) {
      setSelectedClients([]);
    }
  };

  const validateForm = () => {
    const errors = [];

    if (!body.title.trim()) {
      errors.push('O título do template é obrigatório');
    }

    // Validação das tarefas
    const tasksToSave = taskSectionRef.current?.getTasks();
    if (!tasksToSave || tasksToSave.length === 0) {
      errors.push('Adicione pelo menos uma tarefa ao template');
    }

    // Validação de cliente e catálogo de serviço
    const hasSubcategory = body.subcategory.id !== 0;
    const hasClients = allClientsSelected || selectedClients.length > 0;

    // Validação para cliente + catalogo
    if (hasSubcategory && !hasClients) {
      errors.push('Selecione pelo menos um cliente ou marque "Todos os clientes" quando um catálogo estiver selecionado');
    } else if (!hasSubcategory && hasClients) {
      errors.push('Selecione um catálogo de serviço quando clientes estiverem selecionados');
    }

    return errors;
  };

  const handleSaveTemplate = async () => {
    setIsSaving(true);
    try {
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => showErrorNotification(error));
        return;
      }

      const tasksToSave = taskSectionRef.current?.getTasks() || [];

      const templateData = {
        title: body.title,
        description: body.description,
        applyToAllCustomers: allClientsSelected,
        customersIds: allClientsSelected ? [] : selectedClients,
        taskTemplateItems: tasksToSave.map(task => ({
          title: task.title,
          isGroupTitle: task.isGroupTitle || false,
          description: task.description || '',
          deadlineDays:
            (task.deadlineDays === null || task.deadlineDays === undefined || task.deadlineDays === '' ||
              task.days_to_complete === null || task.days_to_complete === undefined || task.days_to_complete === '')
              ? null
              : task.deadlineDays !== undefined && task.deadlineDays !== ''
                ? parseInt(task.deadlineDays)
                : parseInt(task.days_to_complete),
          status: false,
          agent: task.agent ? { id: task.agent.id } : null
        }))
      };

      if (body.serviceCatalog.id !== 0 && body.category.id !== 0 && body.subcategory.id !== 0) {
        templateData.serviceCatalog = { id: body.serviceCatalog.id };
        templateData.category = { id: body.category.id };
        templateData.subcategory = { id: body.subcategory.id };
      } else {
        templateData.serviceCatalog = null;
        templateData.category = null;
        templateData.subcategory = null;
      }

      console.log('Sending to API (clone):', templateData);
      const response = await TaskTemplateService.create(templateData);

      if (response && !response.status) {
        showSuccessNotification('Template clonado com sucesso!');
        navigate('/settings/task-templates');
      } else {
        throw new Error(response?.userMessage || 'Erro ao clonar template');
      }
    } catch (error) {
      console.error('Error cloning template:', error);
      showErrorNotification(error.message || 'Erro ao clonar template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const getTreeSelectKey = () => {
    return selectedSubcategory
      ? `treeselect-clone-${selectedSubcategory.serviceCatalogId}-${selectedSubcategory.categoryId}-${selectedSubcategory.id}`
      : `treeselect-clone-empty-${Date.now()}`;
  };

  return (
    <>
      <SuccessRequest
        message={success}
        showAlert={handleModals.success}
        setShowAlert={() => toggleModal('success', false)}
      />
      <FailedRequest
        message={error.description}
        showAlert={handleModals.error}
        setShowAlert={() => toggleModal('error', false)}
      />

      <Row className="g-3">
        <Col xl={9}>
          <FalconComponentCard>
            <Flex alignItems="center">
              <Card.Header>
                <Flex justifyContent="between" alignItems="center" className="w-100">
                  <IconButton
                    onClick={handleCancel}
                    variant="falcon-default"
                    size="sm"
                    icon="arrow-left"
                    className="me-2"
                  />
                  <h3>Clonar Template</h3>
                </Flex>
              </Card.Header>
            </Flex>

            <FalconComponentCard.Body>
              {isLoading ? (
                <>
                  <Skeleton height={44} baseColor="#5E6E82" className="mb-4" style={{ width: "65%" }} />
                  <Skeleton height={200} baseColor="#5E6E82" className="mb-4" style={{ width: "65%" }} />
                  <Skeleton height={80} baseColor="#5E6E82" />
                  <Skeleton height={80} baseColor="#5E6E82" className="mb-4" />
                  <Skeleton height={60} baseColor="#5E6E82" className="mb-4 w-50" />
                  <Skeleton height={200} baseColor="#5E6E82" className="mb-4" />
                </>
              ) : (
                <Form validated={validated} ref={form}>
                  <Row className="mb-3">
                    <Col lg={8}>
                      <Form.Group>
                        <Form.Label className="mb-2">
                          Título do Template <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ex: Template para Desenvolvimento de Software"
                          required
                          value={body.title}
                          onChange={e => handleSetBody('title', e.target.value)}
                          className="py-2"
                        />
                        <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          Insira um título
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col lg={8}>
                      <Form.Group>
                        <Form.Label className="mb-2">Descrição do Template</Form.Label>
                        <Form.Control
                          as="textarea"
                          placeholder="Descreva quando e como este template deve ser usado..."
                          rows={6}
                          value={body.description}
                          onChange={e => handleSetBody('description', e.target.value)}
                          className="py-2"
                        />
                        <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          Insira uma descrição
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="mb-2">
                          Catálogo de serviço
                        </Form.Label>
                        <div className="p-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className="flex-grow-1">
                              <TreeSelect
                                key={getTreeSelectKey()}
                                data={serviceCatalogs}
                                onSelect={handleSelectServiceCatalog}
                                initialValue={selectedSubcategory ? [
                                  selectedSubcategory.serviceCatalogId,
                                  selectedSubcategory.categoryId,
                                  selectedSubcategory.id
                                ] : []}
                                showPlaceholder={true}
                              />
                            </div>
                            {selectedSubcategory && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={clearSubcategorySelection}
                                title="Limpar seleção"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            )}
                          </div>
                        </div>
                        {process.env.NODE_ENV === 'development' && (
                          <small className="text-muted">
                            Debug: {selectedSubcategory
                              ? `Selecionado: ${selectedSubcategory.serviceCatalogId}-${selectedSubcategory.categoryId}-${selectedSubcategory.id}`
                              : 'Nenhuma seleção'}
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-0">
                    <Col>
                      <div className="mb-3">
                        <Form.Label className="mb-2">
                          Defina quais clientes podem usar este template
                        </Form.Label>
                        <div className="border rounded p-3 mb-3">
                          <Flex alignItems="center" className="mb-2" justifyContent="between">
                            <div>
                              <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                              <span className="me-2">Todos os clientes</span>
                            </div>
                            <Form.Check
                              type="switch"
                              name="clientAssociation"
                              id="allClients"
                              checked={allClientsSelected}
                              onChange={toggleAllClients}
                              className="fw-semibold"
                            />
                          </Flex>
                          {allClientsSelected && (
                            <p className="mb-0 ms-4">
                              Este template estará disponível para todos os clientes
                            </p>
                          )}
                        </div>

                        {!allClientsSelected && (
                          <div className="border rounded p-3">
                            <p>Clientes Disponíveis</p>
                            <div className="p-2">
                              <MultiSelect
                                placeholder="Selecione os clientes..."
                                options={customers.content.map(client => ({
                                  value: client.id,
                                  label: client.name || client.fantasyName
                                }))}
                                value={selectedClients.map(clientId => {
                                  const client = customers.content.find(c => c.id === clientId);
                                  return {
                                    value: clientId,
                                    label: client ? (client.name || client.fantasyName) : clientId
                                  };
                                })}
                                onChange={selectedOptions =>
                                  setSelectedClients(selectedOptions.map(option => option.value))
                                }
                                isSearchable
                                isMulti
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label className="mb-2">
                          Tarefas do Template <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="border rounded p-3">
                          <TemplateTaskSection
                            ref={taskSectionRef}
                            onCountChange={() => { }}
                            initialTasks={initialTasks}
                            tasksLoaded={tasksLoaded}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Flex justifyContent="end" gap={2} className="pt-3" alignItems="center">
                        <Button
                          onClick={handleCancel}
                          variant="falcon-default"
                          icon="arrow-left"
                          disabled={isSaving}
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleSaveTemplate}
                          className="ms-1"
                          variant="primary"
                          disabled={isSaving}
                        >
                          {isSaving ? 'Clonando...' : 'Clonar Template'}
                          <FontAwesomeIcon icon={faCopy} className="ms-1" />
                        </Button>
                      </Flex>
                    </Col>
                  </Row>
                </Form>
              )}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
        <Col xl={3}>
          <TaskTemplateExplanation />
        </Col>
      </Row>
    </>
  );
};

export default TaskTemplateClone;